/**
 * Synced-post embed registry and URL classifier (SCH-2145, plan unit U1 of
 * docs/plans/2026-09-23-1506-feat-synced-post-embeds-plan.md).
 *
 * Blackbaud-synced news and announcements carry `<iframe>`, `<embed>`,
 * `<object>`, `<video>`/`<source>` and `<script src>` tags. This module is the
 * one source of truth for which hosts are known, what kind of content each
 * one serves, and how every surface renders it:
 *
 *   - the backend classifies each embed at sync and stores the result on
 *     `ContentRecord.metadata.embeds` (`SyncedEmbedEntrySchema`);
 *   - mobile renders by `mode` and falls back to `classifyEmbedUrl` for posts
 *     not yet classified;
 *   - the web sanitizer lets registry iframes through.
 *
 * It is separate from the per-school custom-pages allowlist
 * (`DEFAULT_EMBED_ALLOWLIST`), which gates admin-authored pages.
 *
 * Pure module: no network and no Node-only APIs. It runs on the backend, on
 * React Native and in the browser, and relies only on the global `URL`.
 */

import { z } from 'zod'
import { EMBED_HEIGHT_MAX, EMBED_HEIGHT_MIN } from './page'

// ============================================
// Enums
// ============================================

/**
 * How a surface renders an embed:
 *   - `live`: the embed itself, in a card frame.
 *   - `video-card`: a native thumbnail card that opens outside the app.
 *   - `open-card`: a card that opens the source URL.
 *   - `extract`: the backend copies the file into an attachment.
 *   - `ignore`: render nothing (loader scripts, Flash, plugin assets).
 */
export const SYNCED_EMBED_MODES = ['live', 'video-card', 'open-card', 'extract', 'ignore'] as const
export const SyncedEmbedModeSchema = z.enum(SYNCED_EMBED_MODES)
export type SyncedEmbedMode = z.infer<typeof SyncedEmbedModeSchema>

export const SYNCED_EMBED_KINDS = [
  'canva',
  'smore',
  'issuu',
  'pics-io',
  'google-docs',
  'fliphtml5',
  'wakelet',
  'smugmug',
  'vidigami',
  'scribe',
  'facebook',
  'custom-html',
  'youtube',
  'vimeo',
  'bb-cdn-pdf',
  'bb-cdn-media',
  'bb-page',
  'video-other',
  'script-plugin',
  'unknown'
] as const
export const SyncedEmbedKindSchema = z.enum(SYNCED_EMBED_KINDS)
export type SyncedEmbedKind = z.infer<typeof SyncedEmbedKindSchema>

// ============================================
// Registry
// ============================================

/** Tag name (lower-cased) and `type=` attribute of the element that carried the URL. */
export interface SyncedEmbedHint {
  tag?: string
  type?: string
}

/**
 * One way a URL can match a kind. `hosts` use the custom-pages allowlist
 * wildcard semantics: a literal host matches exactly; `*.x` matches any
 * subdomain of `x` but not `x` itself. A rule with no `hosts` matches any
 * host. `path` and `when` narrow the match further.
 */
export interface SyncedEmbedRule {
  hosts?: readonly string[]
  path?: RegExp
  when?: (hint: SyncedEmbedNormalizedHint) => boolean
}

export interface SyncedEmbedRegistryEntry {
  kind: SyncedEmbedKind
  mode: SyncedEmbedMode
  /** User-facing label with its article, e.g. "a Canva design". */
  label: string
  /** Default width / height, used when the markup gives no ratio. */
  defaultAspect: number
  rules: readonly SyncedEmbedRule[]
}

export interface SyncedEmbedNormalizedHint {
  tag: string
  type: string
}

const PORTRAIT_PAGE = 8.5 / 11
const WIDESCREEN = 16 / 9

function isBlackbaudPdfCandidate(hint: SyncedEmbedNormalizedHint): boolean {
  if (hint.type === 'application/pdf') return true
  if (hint.tag !== 'embed' && hint.tag !== 'object') return false
  return !hint.type.startsWith('image/') && !hint.type.startsWith('video/')
}

/**
 * Every host class in the 2026-09-22 fleet inventory (plan Appendix). Order
 * matters: the first matching rule wins, so the path-based `ignore` rules sit
 * ahead of the host rules they would otherwise fall into (`player.vimeo.com`,
 * `.swf` on any host, `bb-cdn-pdf` before `bb-cdn-media`).
 */
export const SYNCED_EMBED_REGISTRY: readonly SyncedEmbedRegistryEntry[] = [
  {
    kind: 'script-plugin',
    mode: 'ignore',
    label: 'a script',
    defaultAspect: WIDESCREEN,
    rules: [
      { hosts: ['player.vimeo.com'], path: /^\/api\/player\.js$/i },
      { hosts: ['www.instagram.com', 'instagram.com'], path: /\/embed\.js$/i },
      { hosts: ['embed-assets.wakelet.com', 'cdn.jsdelivr.net', 'sprx.cloud', 'lh3.googleusercontent.com'] },
      { path: /\.swf$/i }
    ]
  },
  {
    kind: 'canva',
    mode: 'live',
    label: 'a Canva design',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['www.canva.com', 'canva.com'] }]
  },
  {
    kind: 'smore',
    mode: 'live',
    label: 'a Smore newsletter',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['app.smore.com', 'secure.smore.com', '*.smore.com'] }]
  },
  {
    kind: 'issuu',
    mode: 'live',
    label: 'an Issuu publication',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['e.issuu.com', 'issuu.com'] }]
  },
  {
    kind: 'pics-io',
    mode: 'live',
    label: 'a Pics.io gallery',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['show.pics.io'] }]
  },
  {
    kind: 'google-docs',
    mode: 'live',
    label: 'a Google document',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['docs.google.com', 'drive.google.com'] }]
  },
  {
    kind: 'fliphtml5',
    mode: 'live',
    label: 'a FlipHTML5 publication',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['online.fliphtml5.com'] }]
  },
  {
    kind: 'wakelet',
    mode: 'live',
    label: 'a Wakelet collection',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['embed.wakelet.com'] }]
  },
  {
    kind: 'smugmug',
    mode: 'live',
    label: 'a SmugMug gallery',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['*.smugmug.com', 'api.smugmug.com'] }]
  },
  {
    kind: 'vidigami',
    mode: 'live',
    label: 'a Vidigami gallery',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['public.vidigami.com'] }]
  },
  {
    kind: 'scribe',
    mode: 'live',
    label: 'a Scribe guide',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['scribehow.com'] }]
  },
  {
    kind: 'facebook',
    mode: 'live',
    label: 'a Facebook post',
    defaultAspect: 1,
    rules: [{ hosts: ['www.facebook.com'], path: /^\/plugins\// }]
  },
  {
    kind: 'custom-html',
    mode: 'live',
    label: 'an interactive page',
    defaultAspect: PORTRAIT_PAGE,
    // `*.github.io` is deliberately not wildcarded: any GitHub user can publish there.
    rules: [{ hosts: ['mglavach.github.io', 'salem.opalstacked.com'] }]
  },
  {
    kind: 'youtube',
    mode: 'video-card',
    label: 'a YouTube video',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtu.be'] }]
  },
  {
    kind: 'vimeo',
    mode: 'video-card',
    label: 'a Vimeo video',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['player.vimeo.com', 'vimeo.com'] }]
  },
  {
    kind: 'bb-cdn-pdf',
    mode: 'extract',
    label: 'a PDF',
    defaultAspect: PORTRAIT_PAGE,
    rules: [{ hosts: ['*.myschoolcdn.com'], when: isBlackbaudPdfCandidate }]
  },
  {
    kind: 'bb-cdn-media',
    mode: 'open-card',
    label: 'a Blackbaud media file',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['*.myschoolcdn.com'] }]
  },
  {
    kind: 'bb-page',
    mode: 'open-card',
    label: 'a Blackbaud page',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['*.myschoolapp.com'] }]
  },
  {
    kind: 'video-other',
    mode: 'open-card',
    label: 'a video',
    defaultAspect: WIDESCREEN,
    rules: [{ hosts: ['livestream.com', 'www.hudl.com', 'w3.cdn.anvato.net', 'up.anv.bz', 'c.brightcove.com'] }]
  }
]

/** Fallback for hosts not on the registry, unparseable URLs and non-web schemes. */
export const SYNCED_EMBED_UNKNOWN: SyncedEmbedRegistryEntry = {
  kind: 'unknown',
  mode: 'open-card',
  label: 'embedded content',
  defaultAspect: WIDESCREEN,
  rules: []
}

/**
 * Registry entry for a kind. Kinds this build does not know (written by a
 * newer backend) resolve to the `unknown` entry.
 */
export function getSyncedEmbedKindInfo(kind: string): SyncedEmbedRegistryEntry {
  return SYNCED_EMBED_REGISTRY.find(entry => entry.kind === kind) ?? SYNCED_EMBED_UNKNOWN
}

/** Host patterns for a kind (wildcards included), e.g. for a WebView navigation guard. */
export function getSyncedEmbedHostPatterns(kind: string): readonly string[] {
  return getSyncedEmbedKindInfo(kind).rules.flatMap(rule => rule.hosts ?? [])
}

/**
 * Host matcher with the same semantics as the backend's custom-pages
 * `matchEmbedHost`: literal patterns match exactly; `*.x` matches any direct
 * or nested subdomain of `x`, never bare `x`.
 */
export function matchSyncedEmbedHost(hostname: string, patterns: readonly string[]): boolean {
  const host = hostname.toLowerCase()
  for (const pattern of patterns) {
    if (pattern.startsWith('*.')) {
      if (host.endsWith('.' + pattern.slice(2))) return true
    } else if (host === pattern) {
      return true
    }
  }
  return false
}

// ============================================
// Classifier
// ============================================

export interface SyncedEmbedClassification {
  /** The https form of the URL; empty when the input is not an http(s) URL. */
  url: string
  /** Lower-cased hostname; empty when the input is not an http(s) URL. */
  host: string
  kind: SyncedEmbedKind
  mode: SyncedEmbedMode
}

const UNPARSEABLE: SyncedEmbedClassification = { url: '', host: '', kind: 'unknown', mode: 'open-card' }

/**
 * Normalize an embed URL to https. Protocol-relative (`//x`) and `http:` URLs
 * become `https:`. Any other scheme (`javascript:`, `data:`, relative paths,
 * garbage) returns null.
 */
function toHttpsUrl(raw: string): URL | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const withScheme = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
  let parsed: URL
  try {
    parsed = new URL(withScheme)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
  if (!parsed.hostname) return null
  parsed.protocol = 'https:'
  // Credentials never belong in a stored or rendered embed URL.
  parsed.username = ''
  parsed.password = ''
  return parsed
}

function normalizeHint(hint: SyncedEmbedHint | undefined): SyncedEmbedNormalizedHint {
  return {
    tag: (hint?.tag ?? '').trim().toLowerCase(),
    // Drop MIME parameters: `application/pdf; charset=binary` is still a PDF.
    type: (hint?.type ?? '').split(';')[0]!.trim().toLowerCase()
  }
}

function ruleMatches(rule: SyncedEmbedRule, host: string, path: string, hint: SyncedEmbedNormalizedHint): boolean {
  if (rule.hosts && !matchSyncedEmbedHost(host, rule.hosts)) return false
  if (rule.path && !rule.path.test(path)) return false
  if (rule.when && !rule.when(hint)) return false
  return true
}

/**
 * Classify one embed URL against the registry. Never throws. A URL that is not
 * http(s) classifies as `unknown` / `open-card` with an empty `url`, so a
 * caller never renders or opens it. `hint` separates Blackbaud CDN PDFs
 * (`extract`) from other Blackbaud CDN media (`open-card`).
 */
export function classifyEmbedUrl(url: string, hint?: SyncedEmbedHint): SyncedEmbedClassification {
  const parsed = toHttpsUrl(url)
  if (!parsed) return UNPARSEABLE
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '')
  const normalizedHint = normalizeHint(hint)
  const entry =
    SYNCED_EMBED_REGISTRY.find(candidate =>
      candidate.rules.some(rule => ruleMatches(rule, host, parsed.pathname, normalizedHint))
    ) ?? SYNCED_EMBED_UNKNOWN
  return { url: parsed.href, host, kind: entry.kind, mode: entry.mode }
}

// ============================================
// Sizing
// ============================================

/**
 * Frame height for a given width and aspect (width / height), clamped to the
 * same bounds as the custom-pages embed block.
 */
export function syncedEmbedHeightForWidth(width: number, aspect: number): number {
  const valid = width > 0 && Number.isFinite(width) && aspect > 0 && Number.isFinite(aspect)
  const height = valid ? Math.round(width / aspect) : EMBED_HEIGHT_MIN
  return Math.min(EMBED_HEIGHT_MAX, Math.max(EMBED_HEIGHT_MIN, height))
}

// ============================================
// Vimeo
// ============================================

/**
 * The vimeo.com page for a Vimeo embed or page URL: `https://vimeo.com/<id>`,
 * or `https://vimeo.com/<id>/<hash>` for an unlisted video's `?h=` hash (the
 * page 404s without it). Returns null for anything that is not a Vimeo video
 * URL.
 */
export function vimeoLinkFromEmbedUrl(url: string): string | null {
  const parsed = toHttpsUrl(url)
  if (!parsed) return null
  const host = parsed.hostname.toLowerCase()
  let match: RegExpMatchArray | null = null
  if (host === 'player.vimeo.com') {
    match = parsed.pathname.match(/^\/video\/(\d+)\/?$/)
  } else if (host === 'vimeo.com' || host === 'www.vimeo.com') {
    match = parsed.pathname.match(/^\/(\d+)(?:\/([a-z0-9]+))?\/?$/i)
  }
  if (!match) return null
  const id = match[1]!
  const hash = parsed.searchParams.get('h') ?? match[2] ?? null
  return hash && /^[a-z0-9]+$/i.test(hash) ? `https://vimeo.com/${id}/${hash}` : `https://vimeo.com/${id}`
}

// ============================================
// metadata.embeds entry
// ============================================

/**
 * One entry of `ContentRecord.metadata.embeds`, written by the backend at
 * sync (R1). Lenient on purpose: an installed mobile app must `safeParse`
 * entries from a newer backend, so `kind` is any string and an unrecognised
 * `mode` falls back to `open-card`. Mobile renders by `mode`.
 */
const syncedEmbedEntryShape = {
  tag: z.string(),
  url: z.string(),
  host: z.string(),
  kind: z.string(),
  mode: SyncedEmbedModeSchema.catch('open-card'),
  /** False for `unknown` and for an `extract` entry whose extraction failed. */
  handled: z.boolean(),
  /** Why enrichment did not complete, e.g. a transient download failure. */
  reason: z.string().optional(),
  /** Enrichment attempts so far; transient failures retry below a cap. */
  attempts: z.number().int().nonnegative().optional(),
  // Vimeo details, fetched at sync.
  title: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  durationSeconds: z.number().nonnegative().optional(),
  linkUrl: z.string().optional()
}

export const SyncedEmbedEntrySchema = z.object(syncedEmbedEntryShape).passthrough()
export type SyncedEmbedEntry = z.infer<typeof SyncedEmbedEntrySchema>

export const SyncedEmbedEntriesSchema = z.array(SyncedEmbedEntrySchema)

/**
 * Most embed entries a feed reader sends per post. Real posts carry a
 * handful; the cap bounds the payload if a body ever holds hundreds.
 */
export const FEED_SYNCED_EMBEDS_MAX = 50

/**
 * One `metadata.embeds` entry as the feed readers send it to the apps
 * (`object.embeds` on the flat and notification feeds,
 * `contentRecord.embeds` on activity detail). The rendering fields only:
 * unknown keys are stripped, and so are the sync-only `reason` and
 * `attempts`. The rest of `ContentRecord.metadata` never leaves the backend.
 */
export const FeedSyncedEmbedSchema = z.object(syncedEmbedEntryShape).omit({ reason: true, attempts: true })
export type FeedSyncedEmbed = z.infer<typeof FeedSyncedEmbedSchema>
